import { getAuth, signInWithCustomToken } from "@firebase/auth";
import { Alert, AlertTitle, Button, CircularProgress, Container, Divider, Link, Stack, TextField, Typography } from '@mui/material';
import { Logo } from 'fni-components-next';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import * as yup from 'yup';
import { coreActions } from '../../redux/CoreSlice/core';
import { authActions, authSelectors } from '../modules/auth';
import { notificationsActions, notificationsSelectors } from '../../redux/NotificationsSlice/notifications';
import getBrowserFingerprint from '../services/BrowserFingerprint';
import MfaModal from './MfaModal';
import SamlLoginButton from './SamlLoginButton';
import { getSAMLStatus, getSAMLErrorMessage } from '../services/samlService';

export default function LoginPage() {

    const styles = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    };

    const auth = getAuth();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { code } = useParams();
    const [params] = useSearchParams();

    const mfaState = useSelector(authSelectors.mfaState);
    const activeNotification = useSelector(notificationsSelectors.activeNotification);

    const [browserFingerprint, setBrowserFingerprint] = useState('');

    // SAML SSO state
    const [samlStatus, setSamlStatus] = useState({
        samlEnabled: false,
        passwordAuthDisabled: false,
        allowPasswordFallback: true,
        loading: true,
    });
    const [samlError, setSamlError] = useState(null);

    useEffect(() => {
        getBrowserFingerprint().then(fingerprint => {
            setBrowserFingerprint(fingerprint);
        });
    }, []);

    useEffect(() => {
        if (code) {
            localStorage.setItem('code', code);
            dispatch(coreActions.setCode(code));
        }
    }, []);

    // Fetch active notification on component mount
    useEffect(() => {
        dispatch(notificationsActions.getActiveNotification());
    }, [dispatch]);

    // Check for SAML error in URL params
    useEffect(() => {
        const samlErrorParam = params.get('samlError');
        if (samlErrorParam) {
            setSamlError(getSAMLErrorMessage(samlErrorParam));
            // Clear the error from URL without page reload
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
        }
    }, [params]);

    // Check SAML status for this tenant
    useEffect(() => {
        if (code) {
            getSAMLStatus(code).then(status => {
                setSamlStatus({
                    ...status,
                    loading: false,
                });
            }).catch(() => {
                setSamlStatus(prev => ({ ...prev, loading: false }));
            });
        } else {
            setSamlStatus(prev => ({ ...prev, loading: false }));
        }
    }, [code]);

    const handleClose = () => {
        dispatch(authActions.resetMfaState());
    };

    const {
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        setFieldValue, // <--- Add setFieldValue here
        errors,
        touched,
        dirty,
        isSubmitting,
    } = useFormik({
        initialValues: { email: '', password: '', remember: true, mfaMethodOverride: null },
        validationSchema: yup.object({
            email: yup.string().email('Must be valid email').required('Required'),
            password: yup.string().required('Required'),
            verificationCode: yup.string().trim()
                .matches(/^[0-9]{6}$/, 'Must be 6 digits'),
            remember: yup.boolean(),
            mfaCode: yup.object(), // <--- Add mfaCode to schema for Formik to track
            mfaMethodOverride: yup.string().nullable(), // <--- Add mfaMethodOverride
        }),
        onSubmit: (formValues, { setFieldValue, setSubmitting }) => {
            const username = formValues.email || values.email;
            const password = formValues.password || values.password;
            const mfaCode = values.verificationCode || values.mfaCode; // <--- Change this line
            const fingerprint = browserFingerprint;
            const remember = formValues.remember || values.remember;
            // Use explicit override if set, otherwise use current mfaType from Redux state
            // This ensures EMAIL MFA works correctly (both EMAIL and TOTP use string codes)
            const mfaMethodOverride = formValues.mfaMethodOverride || values.mfaMethodOverride || mfaState.mfaType;

            dispatch(authActions.openMfaVerifyModal(false));

            dispatch(authActions.signInUser({ username, password, mfaCode, fingerprint, remember, code, mfaMethodOverride }))
                .unwrap()
                .then(authResult => {
                    const token = authResult.data.token;
                    const shouldPromptPasskey = authResult.data.hasPasskey === false && authResult.data.passkeySetupSkipped === false;

                    if (process.env.REACT_APP_DISABLE_DOUBLE_LOGIN == 'true') {
                        token.claims?.isFNIAdmin ? navigate('/tenants/list/tenants') : navigate('/');
                    } else if (code) {
                        signInWithCustomToken(auth, token)
                            .then(response => {
                                if (!response.error) {
                                    dispatch(authActions.getRedirect(code))
                                        .unwrap()
                                        .then(response => {
                                            const { redirectUrl, isRegistered, isMigrated, currentTenant } = response.data;

                                            if (!redirectUrl) {
                                                dispatch(authActions.signOutUser());
                                            }
                                            dispatch(authActions.setCurrentTenant(currentTenant));

                                            dispatch(authActions.setUserIsRegistered(isRegistered));

                                            dispatch(authActions.setTenantIsMigrated(isMigrated));

                                            // If we should prompt for passkey setup, store the redirect URL and don't redirect yet
                                            if (shouldPromptPasskey) {
                                                if (redirectUrl.includes('http')) {
                                                    const redirect = new URL(redirectUrl);
                                                    const refnum = params.get('REFNUM');
                                                    params.forEach((v,k) => {
                                                        if (k === 'destination') {
                                                            if (refnum) {
                                                                redirect.searchParams.append(k, `/${v}` + (v.includes('?') ? '&' : '?') + `REFNUM=${refnum}`)
                                                            } else {
                                                                redirect.searchParams.append(k, `/${v}`);
                                                            }
                                                        } else {
                                                            redirect.searchParams.append(k, v);
                                                        }
                                                    });
                                                    dispatch(authActions.setPendingRedirectUrl(redirect.toString()));
                                                } else {
                                                    dispatch(authActions.setPendingRedirectUrl(redirectUrl));
                                                }
                                                // The PasskeySetupPrompt will handle the redirect after user responds
                                                return;
                                            }

                                            // Normal flow - redirect immediately
                                            if (redirectUrl.includes('http')) {
                                                const redirect = new URL(redirectUrl);
                                                const refnum = params.get('REFNUM');
                                                params.forEach((v,k) => {
                                                    if (k === 'destination') {
                                                        if (refnum) {
                                                            redirect.searchParams.append(k, `/${v}` + (v.includes('?') ? '&' : '?') + `REFNUM=${refnum}`)
                                                        } else {
                                                            redirect.searchParams.append(k, `/${v}`);
                                                        }
                                                    } else {
                                                        redirect.searchParams.append(k, v);
                                                    }
                                                });
                                                window.location.href = redirect;
                                            } else {
                                                navigate(redirectUrl);
                                            }
                                        })
                                        .catch(() => {
                                            dispatch(authActions.signOutUser())
                                                .then(() => {
                                                    dispatch(authActions.setErrorMessage('Login Error'));
                                                    navigate('/auth/login/' + code);
                                                });
                                        });
                                };
                            });
                    }

                })
                .catch(error => {
                    switch (error.message) {
                        case 'USER_LOCKED':
                            dispatch(authActions.setErrorMessage('Your user is locked, please wait a few minutes and try again'));
                            setFieldValue('password', '', false);
                            break;
                        case 'INVALID_CREDENTIALS':
                            dispatch(authActions.setErrorMessage('Incorrect username or password'));
                            setFieldValue('password', '', false);
                            break;
                        case 'MFA_INVALID_CODE':
                            setFieldValue('verificationCode', '', false);
                            dispatch(authActions.setErrorMessage('Invalid MFA Code'));
                        case 'MFA_CODE_REQUIRED':
                            dispatch(authActions.openMfaVerifyModal(true));
                            if (error.token) {
                                signInWithCustomToken(auth, error.token).catch(err => {
                                    dispatch(authActions.setErrorMessage('MFA setup failed. Please try again.'));
                                });
                            }
                            break;
                        case 'MFA_NOT_ENABLED':
                            signInWithCustomToken(auth, error.token)
                                .then(response => {
                                    const timer = setTimeout(() => {
                                        if (!response.error) navigate(`/auth/register${params.size ? `?${params.toString()}` : ''}`);
                                    }, 1000);
                                    return () => clearTimeout(timer);
                                });

                            break;
                        case 'FORCE_CHANGE_PASSWORD':
                            signInWithCustomToken(auth, error.token)
                                .then(response => {
                                    const timer = setTimeout(() => {
                                        if (!response.error) {
                                            navigate(`/auth/force-change-password${params.size ? `?${params.toString()}` : ''}`, { state: { oldPassword: values.password } });
                                        }
                                    }, 1000);
                                    return () => clearTimeout(timer);
                                });
                            break;

                    }
                })
                .finally(() => setSubmitting(false));
        },
    });

    const showEmailError = Boolean(errors.email && touched.email && dirty);

    const showPasswordError = Boolean(errors.password && touched.password && dirty);

    const showMfaError = Boolean(errors.verificationCode && touched.verificationCode && dirty);

    // Determine what to show based on SAML configuration
    const showPasswordForm = !samlStatus.passwordAuthDisabled || samlStatus.allowPasswordFallback;
    const showSamlButton = samlStatus.samlEnabled;
    const showDivider = showPasswordForm && showSamlButton;

    return (
        <Container sx={styles} maxWidth='md'>

            {activeNotification && (
                <Alert severity={activeNotification.severity} sx={{ width: '100%', mb: 2 }}>
                    <AlertTitle>{activeNotification.title}</AlertTitle>
                    <ReactMarkdown rehypePlugins={[rehypeRaw, rehypeSanitize]}>{activeNotification.message}</ReactMarkdown>
                </Alert>
            )}

            {samlError && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }} onClose={() => setSamlError(null)}>
                    {samlError}
                </Alert>
            )}

            <Logo code={code} width='35%' />

            <Stack spacing={2} padding={3} margin={3} sx={{ width: '100%' }}>

                {/* Show loading indicator while checking SAML status */}
                {samlStatus.loading ? (
                    <Stack alignItems="center" spacing={2} py={4}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" color="text.secondary">
                            Loading...
                        </Typography>
                    </Stack>
                ) : (
                    <>
                        {/* SAML SSO Button */}
                        {showSamlButton && (
                            <SamlLoginButton
                                tenantCode={code}
                                loading={samlStatus.loading}
                                disabled={isSubmitting}
                            />
                        )}

                        {/* Divider between SSO and password form */}
                        {showDivider && (
                            <Divider sx={{ my: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    or sign in with email
                                </Typography>
                            </Divider>
                        )}

                        {/* Password Form - only show if not SAML-only mode */}
                        {showPasswordForm && (
                            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                                <Stack spacing={2}>
                                    <TextField
                                        name='email'
                                        label='Email'
                                        autoFocus={!showSamlButton}
                                        value={values.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={showEmailError}
                                        helperText={showEmailError ? errors.email : null}
                                    />

                                    <TextField
                                        name='password'
                                        label='Password'
                                        type='password'
                                        value={values.password}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={showPasswordError}
                                        helperText={showPasswordError ? errors.password : null}
                                    />

                                    <Stack flexDirection={{ xs: 'column', sm: 'row' }}
                                        justifyContent={{ xs: 'center', sm: 'space-between' }} >

                                        <Link onClick={() => navigate('/auth/forgot-password')}>Forgot Password?</Link>

                                        <Button disabled={isSubmitting} type='submit'>Login</Button>

                                    </Stack>
                                </Stack>
                            </form>
                        )}

                        {/* SAML-only mode message */}
                        {samlStatus.passwordAuthDisabled && !samlStatus.allowPasswordFallback && (
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                This organization uses Single Sign-On (SSO) for authentication.
                            </Typography>
                        )}
                    </>
                )}

                <MfaModal
                    open={mfaState.modal.verifyOpen}
                    onClose={handleClose}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    values={values}
                    handleSubmit={handleSubmit}
                    error={showMfaError}
                    helperText={showMfaError ? errors.verificationCode : null}
                />

            </Stack>

        </Container>
    );
}
